import { getFinancialHeadersForDebtSchedule } from '../../../../utils/dateUtils';
import dayjs from 'dayjs';
import { convertToFirstLetterCapital, formatRatioValue, safeToNumber } from '../../../../utils/utils';
import { filterFinancialHeadersWithoutClassificationName, isProvisionalHeader } from '../../../../store/investor/constants';

const AICA_AI_PROFILE_MAIN = 'aicaAIProfileMain';
const AICA_AI_PROFILE_MAIN_V2 = 'aicaAIProfileMainV2';
const AICA_FETCH_ORG_INFO = 'orgInfo';
const POLUS_FETCH_AUDITED_FINANCIAL_BALANCE_SHEET = 'auditedFinancialBalanceSheet';
const POLUS_FETCH_AUDITED_FINANCIAL_PROFIT_AND_LOSS = 'auditedFinancialProfitAndLoss';
const POLUS_FETCH_AUDITED_FINANCIAL_UNSECURED_LOAN = 'auditedFinancialUnsecuredLoan';
const UW_DEBT_SCHEDULE_DATA = 'debtSchedule';
const TOP_5_VENDOR_EXPENSE_LIST = 'top5VendorExpenseList';
const TOP_5_REVENUE_SOURCE_LIST = 'top5RevenueSourceList';
const DOMESTIC_REVENUE_ACROSS_STATES = 'domesticRevenueAcrossStates';
export const COMPANY_METRICS_DATA = 'companyMetricsData';
const FINANCIAL_RATIO_DATA = 'financialRatioData';

const YELLOW = '#FFD044';
const GenerateError = " Couldn't Generate [Enter Details Here] ";
const DataNotAvl = '[Data Not Available]';

const UW_DEBT_SCHEDULE_DATA_HEADERS = ['lenderName', 'typeOfDebtLoan', 'sanctionedAmount', 'outstandingBalance'];

const UW_DEBT_SCHEDULE_DATA_HEADER_NAMES = {
    lenderName: 'Lender Name',
    typeOfDebtLoan: 'Type of Debt/Loan',
    sanctionedAmount: 'Sanctioned Amount (Crs.)',
    outstandingBalance: 'O/S Balance as of Date (Crs.)',
};

/** -----****Initial Data for One Pager Summary****---
 * 1. initialiseDealSummaryEditor : for data initialised using api data.
 * -> 4 children functions to get specific data from the api
 * 2. updatePreferredTermsInEditorData: for data initialised from deal terms step 2 inputs.
 */

export function initialiseDealSummaryEditor(apiData: any, formData: null | Object = {}) {
    let uw_debt_schedule_data = apiData?.[UW_DEBT_SCHEDULE_DATA] ?? null;
    let ai_profile_main_data = apiData?.[AICA_AI_PROFILE_MAIN] ?? null;
    let ai_profile_main_data_v2 = apiData?.[AICA_AI_PROFILE_MAIN_V2] ?? null;
    let audited_financial_profit_and_loss = apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_PROFIT_AND_LOSS] ?? null;
    let audited_financial_balance_sheet = apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_BALANCE_SHEET] ?? null;
    let audited_financial_unsecured_loan = processAuditedFinancialUnsecuredLoanData(
        apiData?.[POLUS_FETCH_AUDITED_FINANCIAL_UNSECURED_LOAN] ?? null,
    );
    let orgInfo = apiData?.[AICA_FETCH_ORG_INFO] ?? null;
    let companyMetricsData = apiData?.[COMPANY_METRICS_DATA]?.borrowerFinancials ?? {};
    let companyMetricsLastUpdatedDate =
        companyMetricsData?.lastUpdatedAt && dayjs(companyMetricsData?.lastUpdatedAt).isValid()
            ? dayjs(companyMetricsData?.lastUpdatedAt).format('DD MMM YYYY')
            : null;

    let topFiveVendorExpense = apiData?.[TOP_5_VENDOR_EXPENSE_LIST]?.[0] ?? null;
    let topFiveRevenueSource = apiData?.[TOP_5_REVENUE_SOURCE_LIST]?.[0] ?? null;
    let domesticRevenueAcrossStates = apiData?.[DOMESTIC_REVENUE_ACROSS_STATES] ?? null;
    let financialRatioData = apiData?.[FINANCIAL_RATIO_DATA] ?? null;
    let encumberedCashBalance = companyMetricsData?.encumberedCashBalance ?? null;

    let uw_debt_schedule_data_headers =
        uw_debt_schedule_data?.headers?.filter((header) => UW_DEBT_SCHEDULE_DATA_HEADERS?.includes(header?.key)) ?? [];
    let table_uwDebtSchedule: any = null,
        table_managementProfile: any = null,
        table_dealSummary: any = null,
        table_preferredDealTerms: any = null,
        table_financialSummary: any = null;

    function getDealSummaryData() {
        let dealSummary = {
            key: 'table_dealSummary',
            type: 'tableWithoutHeaders',
            columns: 2,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: [
                        {
                            type: 'table-header',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [{ text: 'Legal Entity Name' }],
                                },
                            ],
                        },
                        {
                            type: 'table-header',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [
                                        {
                                            type: 'paragraph',
                                            children: [
                                                orgInfo?.orgName
                                                    ? {
                                                          text: `${orgInfo?.orgName ?? ''}`,
                                                          bold: true,
                                                      }
                                                    : {
                                                          text: ` ${DataNotAvl}`,
                                                          bgColor: YELLOW,
                                                          bold: true,
                                                      },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'table-row',
                    children: [
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [{ text: 'Date of incorporation' }],
                                },
                            ],
                        },
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [
                                        ai_profile_main_data?.foundationDate
                                            ? {
                                                  text: `${ai_profile_main_data?.foundationDate ?? ''}`,
                                                  bold: true,
                                              }
                                            : {
                                                  text: `${DataNotAvl}`,
                                                  bgColor: YELLOW,
                                                  bold: true,
                                              },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'table-row',
                    children: [
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [{ text: 'Constitution' }],
                                },
                            ],
                        },
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [
                                        orgInfo?.constitutionType
                                            ? {
                                                  text: `${orgInfo?.constitutionType ?? ''}`,
                                                  bold: true,
                                              }
                                            : {
                                                  text: `${DataNotAvl}`,
                                                  bgColor: YELLOW,
                                                  bold: true,
                                              },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    key: 'sector_industry',
                    type: 'table-row',
                    children: [
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [{ text: 'Sector/Industry' }],
                                },
                            ],
                        },
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [
                                        formData?.['borrowerProfile.companySector'] || ai_profile_main_data_v2?.data?.sectorDetails?.sector
                                            ? {
                                                  text: `${
                                                      formData?.['borrowerProfile.companySector']
                                                          ? formData?.['borrowerProfile.companySector']
                                                          : ai_profile_main_data_v2.data.sectorDetails.sector
                                                  }`,
                                                  bold: true,
                                              }
                                            : {
                                                  text: `${DataNotAvl}`,
                                                  bgColor: YELLOW,
                                                  bold: true,
                                              },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'table-row',
                    children: [
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [{ text: 'Headquarters' }],
                                },
                            ],
                        },
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [
                                        ai_profile_main_data?.location
                                            ? {
                                                  text: `${ai_profile_main_data?.location ?? ''}`,
                                                  bold: true,
                                              }
                                            : {
                                                  text: `${DataNotAvl}`,
                                                  bgColor: YELLOW,
                                                  bold: true,
                                              },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'table-row',
                    children: [
                        {
                            type: 'table-cell',
                            children: [{ text: 'Business Overview' }],
                        },
                        {
                            type: 'table-cell',
                            children: [
                                {
                                    type: 'paragraph',
                                    children: [
                                        ai_profile_main_data_v2?.data?.overview?.overviewContent
                                            ? { text: ai_profile_main_data_v2.data.overview.overviewContent }
                                            : {
                                                  text: `${DataNotAvl}`,
                                                  bgColor: YELLOW,
                                              },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    type: 'table-row',
                    children: [
                        {
                            type: 'table-cell',
                            children: [{ text: 'Previous Investors and Funding Stage' }],
                        },
                        {
                            type: 'table-cell',

                            children: [
                                {
                                    type: 'paragraph',
                                    children: [
                                        {
                                            type: 'unorderedList',
                                            children: [
                                                {
                                                    type: 'list-item',
                                                    children: [
                                                        {
                                                            text: `Investor Name(s): `,
                                                            bold: true,
                                                        },
                                                        ai_profile_main_data_v2?.data?.overview?.keyInvestors?.length
                                                            ? {
                                                                  text: ai_profile_main_data_v2.data.overview.keyInvestors
                                                                      .map((investor: any) => investor)
                                                                      .join(', '),
                                                              }
                                                            : {
                                                                  text: `${DataNotAvl}`,
                                                                  bgColor: YELLOW,
                                                              },
                                                    ],
                                                },
                                                {
                                                    type: 'list-item',
                                                    children: [
                                                        {
                                                            text: `Funding Stage: `,
                                                            bold: true,
                                                        },
                                                        ai_profile_main_data?.fundingInfo?.stage
                                                            ? {
                                                                  text: `${ai_profile_main_data?.fundingInfo?.stage ?? ''}`,
                                                              }
                                                            : {
                                                                  text: `${DataNotAvl}`,
                                                                  bgColor: YELLOW,
                                                              },
                                                    ],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
        };
        dealSummary.rows = dealSummary?.children?.length ?? 0;
        return dealSummary;
    }
    function getManagementProfileData() {
        let managementProfileData = {
            key: 'table_managementProfile',
            type: 'tableWithoutHeaders',
            columns: 2,
            rows: 0,
            children: !!ai_profile_main_data?.directors?.length
                ? ai_profile_main_data?.directors?.map((director: any, index) => ({
                      type: 'table-row',
                      children: [
                          {
                              type: index === 0 ? 'table-header' : 'table-cell',
                              children: [
                                  {
                                      type: 'paragraph',
                                      children: [{ text: director?.name ?? '' }],
                                  },
                              ],
                          },
                          {
                              type: index === 0 ? 'table-header' : 'table-cell',
                              children: [
                                  {
                                      type: 'paragraph',
                                      children: [
                                          {
                                              type: 'unorderedList',
                                              children: [
                                                  {
                                                      type: 'list-item',
                                                      children: [
                                                          {
                                                              text: `Designation: `,
                                                              bold: true,
                                                          },
                                                          director?.designation
                                                              ? {
                                                                    text: `${director?.designation ?? ''}`,
                                                                }
                                                              : {
                                                                    text: DataNotAvl,
                                                                    bgColor: YELLOW,
                                                                    bold: true,
                                                                },
                                                      ],
                                                  },
                                                  {
                                                      type: 'list-item',
                                                      children: [
                                                          {
                                                              text: `Director Since:`,
                                                              bold: true,
                                                          },
                                                          director?.tenureBeginDate
                                                              ? {
                                                                    text: ` ${dayjs(director?.tenureBeginDate).format('MMM YYYY')}`,
                                                                }
                                                              : {
                                                                    text: DataNotAvl,
                                                                    bgColor: YELLOW,
                                                                    bold: true,
                                                                },
                                                      ],
                                                  },
                                                  {
                                                      type: 'list-item',
                                                      children: [
                                                          { text: `Age:`, bold: true },
                                                          director?.age
                                                              ? {
                                                                    text: ` ${director?.age ?? ''}`,
                                                                }
                                                              : {
                                                                    text: DataNotAvl,
                                                                    bgColor: YELLOW,
                                                                    bold: true,
                                                                },
                                                      ],
                                                  },
                                                  { type: 'paragraph', children: [{ text: `` }] },
                                              ],
                                          },
                                      ],
                                  },
                              ],
                          },
                      ],
                  }))
                : [
                      {
                          type: 'table-row',
                          children: [
                              {
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                      },
                                  ],
                              },
                              {
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                      },
                                  ],
                              },
                          ],
                      },
                  ],
        };
        managementProfileData.rows = managementProfileData?.children?.length ?? 0;
        return managementProfileData;
    }
    function getDebtScheduleData() {
        let uwDebtSchedule = {
            key: 'table_uwDebtSchedule',
            type: 'table',
            custom: true,
            columns: uw_debt_schedule_data_headers?.length || UW_DEBT_SCHEDULE_DATA_HEADERS?.length,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: uw_debt_schedule_data_headers?.length
                        ? uw_debt_schedule_data_headers?.map?.((header, index) => ({
                              type: 'table-header',
                              children: [
                                  {
                                      type: 'paragraph',
                                      children: [
                                          {
                                              text: header?.name ?? '',
                                              bold: true,
                                          },
                                      ],
                                  },
                              ],
                              ...(index > 1 && { textAlign: 'right' }),
                          }))
                        : UW_DEBT_SCHEDULE_DATA_HEADERS.map((headerKey, index) => ({
                              type: 'table-header',
                              children: [
                                  {
                                      type: 'paragraph',
                                      children: [
                                          {
                                              text: UW_DEBT_SCHEDULE_DATA_HEADER_NAMES?.[headerKey] ?? '',
                                              bold: true,
                                          },
                                      ],
                                      ...(index > 1 && { textAlign: 'right' }),
                                  },
                              ],
                          })),
                },
                ...(!!uw_debt_schedule_data?.rows?.length && !!uw_debt_schedule_data_headers?.length
                    ? uw_debt_schedule_data?.rows?.map((row: any) => {
                          return {
                              type: 'table-row',
                              children: uw_debt_schedule_data_headers?.map?.((header, index) => ({
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: `${row?.[header?.key] ?? ''}` }],
                                      },
                                  ],
                                  ...(index > 1 && { textAlign: 'right' }),
                              })),
                          };
                      })
                    : [
                          {
                              type: 'table-row',
                              children: UW_DEBT_SCHEDULE_DATA_HEADERS?.map((headerKey, index) => ({
                                  type: 'table-cell',
                                  children: [
                                      {
                                          type: 'paragraph',
                                          children: [{ text: DataNotAvl, bgColor: YELLOW }],
                                      },
                                  ],
                                  ...(index > 1 && { textAlign: 'right' }),
                              })),
                          },
                      ]),
                ...(uw_debt_schedule_data?.rows?.length &&
                ('totalSanctionedAmount' in uw_debt_schedule_data || 'totalOutstandingAmount' in uw_debt_schedule_data)
                    ? [
                          {
                              type: 'table-row',
                              children: [
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [{ text: 'Total' }],
                                          },
                                      ],
                                  },
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [{ text: '' }],
                                          },
                                      ],
                                  },
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [
                                                  {
                                                      text: `${uw_debt_schedule_data?.totalSanctionedAmount ?? '-'}`,
                                                  },
                                              ],
                                          },
                                      ],
                                      textAlign: 'right',
                                  },
                                  {
                                      type: 'table-cell',
                                      children: [
                                          {
                                              type: 'paragraph',
                                              children: [
                                                  {
                                                      text: `${uw_debt_schedule_data?.totalOutstandingAmount ?? '-'}`,
                                                  },
                                              ],
                                          },
                                      ],
                                      textAlign: 'right',
                                  },
                              ],
                          },
                      ]
                    : []),
            ],
        };
        uwDebtSchedule.rows = uwDebtSchedule?.children?.length;
        return uwDebtSchedule;
    }
    function getFinancialSummary() {
        const financialRows = [
            { key: 'Revenue', companyFinancialsKey: 'annualRevenue', label: 'Revenue', table: 'pnl' },
            { key: 'EBITDA', companyFinancialsKey: 'ebitda', label: 'EBITDA', table: 'pnl' },
            { key: 'Profit After Tax', companyFinancialsKey: 'pat', label: 'Profit After Tax', table: 'pnl' },
            { key: 'empty-row', label: '', table: '' },
            { key: 'Net Worth', companyFinancialsKey: 'netWorth', label: 'Net Worth', table: 'balanceSheet' },
            { key: 'Long Term Borrowings', companyFinancialsKey: 'longTermDebt', label: 'Long Term Debt', table: 'unsecuredLoans' },
            { key: 'Short Term Borrowings', companyFinancialsKey: 'shortTermDebt', label: 'Short Term Debt', table: 'unsecuredLoans' },
            {
                key: 'Cash And Cash Equivalents',
                companyFinancialsKey: 'cashAndCashEquivalents',
                label: 'Cash And Cash Equivalents',
                table: 'balanceSheet',
            },
            { key: 'Net Working Capital', companyFinancialsKey: 'workingCapitalGap', label: 'Net Working Capital', table: 'balanceSheet' },
            { key: 'Trade Receivables', companyFinancialsKey: 'tradeReceivables', label: '\tTrade Receivables', table: 'balanceSheet' },
            { key: 'Inventories', companyFinancialsKey: 'inventory', label: '\tInventory', table: 'balanceSheet' },
            { key: 'Trade Payables', companyFinancialsKey: 'tradePayables', label: '\tTrade Payables', table: 'balanceSheet' },
            { key: 'Total Assets', companyFinancialsKey: 'totalAssets', label: 'Total Balance Sheet Size', table: 'balanceSheet' },
        ];

        const FINANCIAL_HEADERS = getFinancialHeadersForDebtSchedule(companyMetricsLastUpdatedDate);

        let table_financialSummary: any = null;
        table_financialSummary = {
            key: 'table_financialSummary',
            type: 'table',
            custom: true,
            columns: FINANCIAL_HEADERS?.length ?? 0,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: FINANCIAL_HEADERS?.map((header, index) => ({
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: `${header?.name ?? '-'}` }],
                                ...(index > 0 && { textAlign: 'right' }),
                            },
                        ],
                    })),
                },
            ],
        };

        financialRows?.forEach((financialRow) => {
            const isEmptyRow = financialRow?.key === 'empty-row';
            let row: any = {
                type: 'table-row',
            };

            let data: any;
            switch (financialRow?.table) {
                case 'pnl':
                    data = audited_financial_profit_and_loss?.[financialRow?.key];
                    break;
                case 'unsecuredLoans':
                    data = audited_financial_unsecured_loan?.rows?.find((row: any) => row?.parent === financialRow?.key);
                    break;
                case 'balanceSheet':
                    data = audited_financial_balance_sheet?.[financialRow?.key];
                    break;
            }

            row.children = [
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: financialRow?.label }],
                        },
                    ],
                },
            ];
            FINANCIAL_HEADERS?.filter?.(filterFinancialHeadersWithoutClassificationName)?.forEach((header) => {
                const isProvisionalHeaderValue = isProvisionalHeader(header);
                const isCashAndCashEquivalentsRow = financialRow?.key === 'Cash And Cash Equivalents';

                const getText = () => {
                    let companyMetricsValue;
                    if (isProvisionalHeaderValue) {
                        // For Cash And Cash Equivalents in Provisional, calculate as encumberedCashBalance + cashBalance
                        if (isCashAndCashEquivalentsRow) {
                            const encumberedCash = safeToNumber(companyMetricsData?.encumberedCashBalance);
                            const cashBalance = safeToNumber(companyMetricsData?.cashBalance);
                            const totalCash = encumberedCash + cashBalance;
                            companyMetricsValue = formatCompanyMetricsValue(totalCash);
                            return `${companyMetricsValue}`;
                        }
                        companyMetricsValue = formatCompanyMetricsValue(companyMetricsData?.[financialRow?.companyFinancialsKey as string]);
                        return `${companyMetricsValue}`;
                    }

                    if (isEmptyRow) return '';
                    return `${data?.[header?.key] ?? '-'}`;
                };

                row.children.push({
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: getText() }],
                        },
                    ],
                    textAlign: 'right',
                });
            });

            table_financialSummary.children.push(row);
        });
        table_financialSummary.rows = table_financialSummary?.children?.length ?? 0;
        return table_financialSummary;
    }

    function getFinancialRatios() {
        if (!financialRatioData || !financialRatioData.headers || !financialRatioData.metrics) {
            return null;
        }

        const ratioMetrics = financialRatioData.metrics;

        // Use Financial Summary headers format for consistency (includes provisional column)
        const FINANCIAL_HEADERS = getFinancialHeadersForDebtSchedule(companyMetricsLastUpdatedDate);

        // Define the ratios to display in order
        const ratioRows = [
            { metricKey: 'Days Payables Outstanding (DPO)', label: 'Days Payables Outstanding (DPO)' },
            { metricKey: 'Days Inventory Outstanding (DIO)', label: 'Days Inventory Outstanding (DIO)' },
            { metricKey: 'Days Sales Outstanding (DSO)', label: 'Days Sales Outstanding (DSO)' },
            { metricKey: 'Cash Conversion Cycle (Days)', label: 'Cash Conversion Cycle (Days)' },
            { metricKey: 'Current Ratio', label: 'Current Ratio' },
            { metricKey: 'Debt to equity', label: 'Debt / Equity' },
            { metricKey: 'Debt / EBITDA', label: 'Debt / EBITDA' },
            { metricKey: 'TOL / TNW', label: 'TOL / TNW' },
            { metricKey: 'DSCR', label: 'Debt Service Coverage Ratio (DSCR)' },
        ];

        // Map companyMetricsData keys to ratio metric keys for provisional data
        const provisionalKeyMap: Record<string, string> = {
            'Days Payables Outstanding (DPO)': 'daysPayablesOutStanding',
            'Days Inventory Outstanding (DIO)': 'daysInventoryOutStanding',
            'Days Sales Outstanding (DSO)': 'daysSalesOutStanding',
            'Cash Conversion Cycle (Days)': 'CashConversionCycleDays',
            'Current Ratio': 'CurrentRatio',
            'Debt to equity': 'DebtToEquity',
            'Debt / EBITDA': 'DebtToEBIDTA',
            'TOL / TNW': 'TOLByTNW',
            DSCR: 'DSCR',
        };

        let table_financialRatios: any = {
            key: 'table_financialRatios',
            type: 'table',
            custom: true,
            columns: FINANCIAL_HEADERS?.length ?? 0,
            rows: 0,
            children: [
                {
                    type: 'table-row',
                    children: FINANCIAL_HEADERS?.map((header, index) => ({
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: `${header?.name ?? '-'}` }],
                                ...(index > 0 && { textAlign: 'right' }),
                            },
                        ],
                    })),
                },
            ],
        };

        ratioRows?.forEach((ratioRow) => {
            const metricData = ratioMetrics[ratioRow.metricKey];
            if (!metricData) return;

            const row: any = {
                type: 'table-row',
            };

            row.children = [
                {
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: ratioRow.label }],
                        },
                    ],
                },
            ];

            // Process each header (skip the first one which is "Particulars (Cr)")
            FINANCIAL_HEADERS?.filter?.(filterFinancialHeadersWithoutClassificationName)?.forEach((header) => {
                const isProvisionalHeaderValue = isProvisionalHeader(header);
                let cellValue = '-';

                if (isProvisionalHeaderValue) {
                    // Get provisional value from companyMetricsData
                    const provisionalKey = provisionalKeyMap[ratioRow.metricKey];
                    if (provisionalKey && companyMetricsData?.[provisionalKey] != null) {
                        cellValue = formatRatioValue(
                            companyMetricsData[provisionalKey],
                            metricData?.unit || '',
                            metricData?.roundOffRequired || false,
                        );
                    }
                } else {
                    // Get audited year value from metric data using year key
                    const yearValue = metricData?.[header?.key];
                    if (yearValue !== null && yearValue !== undefined) {
                        cellValue = formatRatioValue(yearValue, metricData?.unit || '', metricData?.roundOffRequired || false);
                    }
                }

                row.children.push({
                    type: 'table-cell',
                    children: [
                        {
                            type: 'paragraph',
                            children: [{ text: cellValue }],
                        },
                    ],
                    textAlign: 'right',
                });
            });

            table_financialRatios.children.push(row);
        });

        table_financialRatios.rows = table_financialRatios?.children?.length ?? 0;
        return table_financialRatios;
    }

    table_dealSummary = getDealSummaryData();
    table_managementProfile = getManagementProfileData();
    table_uwDebtSchedule = getDebtScheduleData();
    table_financialSummary = getFinancialSummary();
    let table_financialRatios = getFinancialRatios();

    let updatedData = [
        ...(table_dealSummary
            ? [
                  { type: 'headingTwo', children: [{ text: 'Deal Summary' }] },
                  //   { type: 'paragraph', children: [{ text: '\n' }] },
                  table_dealSummary,
              ]
            : []),
        { type: 'paragraph', children: [{ text: '\n' }] },
        ...(table_managementProfile
            ? [
                  { type: 'headingTwo', children: [{ text: 'Management Profile' }] },
                  table_managementProfile,
                  { type: 'paragraph', children: [{ text: '\n' }] },
              ]
            : []),
        ...(table_financialSummary
            ? [
                  { type: 'headingTwo', children: [{ text: 'Financial Summary' }] },
                  table_financialSummary,
                  // { type: 'paragraph', children: [{ text: '\n' }] },
                  ...(encumberedCashBalance
                      ? [
                            {
                                type: 'paragraph',
                                children: [
                                    {
                                        text: `** Out of the total cash and cash equivalents, encumbered cash balance is ${formatCompanyMetricsValue(
                                            encumberedCashBalance,
                                        )} Cr`,
                                        color: '#9D9D9D',
                                        fontSize: '14px',
                                    },
                                ],
                            },
                        ]
                      : []),
                  {
                      type: 'paragraph',
                      children: [
                          {
                              text: `\n*Note: Provisional metrics are updated ${
                                  companyMetricsLastUpdatedDate ? 'as of ' + companyMetricsLastUpdatedDate : ''
                              } basis unaudited data.`,
                              color: '#9D9D9D',
                              fontSize: '14px',
                          },
                      ],
                  },
                  { type: 'paragraph', children: [{ text: '\n' }] },
              ]
            : []),
        ...(table_financialRatios
            ? [
                  { type: 'headingTwo', children: [{ text: 'Financial Ratios' }] },
                  table_financialRatios,
                  { type: 'paragraph', children: [{ text: '\n' }] },
              ]
            : []),
        ...(table_uwDebtSchedule
            ? [
                  {
                      type: 'headingTwo',
                      children: [
                          {
                              text: `Debt Schedule${
                                  uw_debt_schedule_data?.asOfDate
                                      ? ` (as of ${dayjs(uw_debt_schedule_data.asOfDate).format("MMM'YY")})`
                                      : companyMetricsLastUpdatedDate
                                      ? ` (as of ${dayjs(companyMetricsData?.lastUpdatedAt).format("MMM'YY")})`
                                      : ''
                              }`,
                          },
                      ],
                  },
                  table_uwDebtSchedule,
                  { type: 'paragraph', children: [{ text: '\n' }] },
              ]
            : []),
    ];
    // if (formData) {
    //     table_preferredDealTerms = updatePreferredTermsInEditorData(formData);
    //     if (table_preferredDealTerms) {
    //         updatedData = [
    //             ...updatedData,
    //             { type: 'headingThree', children: [{ text: 'Preferred Terms' }] },
    //             table_preferredDealTerms,
    //         ];
    //     }
    // }
    updatedData = [
        ...updatedData,
        {
            type: 'paragraph',
            children: [{ text: '\n' }],
        },
        // {
        //     type: 'image',
        //     url: poweredByAICA,
        //     children: [{ text: '' }],
        //     height: '20px',
        //     justify: 'center',
        // },
    ];
    return updatedData;

    // setTimeout(() => {
    //     setGenerating(false);
    //     setEditorReady(true);
    // }, 200);
}

export const updatePreferredTermsInEditorData = (formData: any) => {
    let table_preferredDealTerms: any = null;

    table_preferredDealTerms = {
        key: 'table_preferredDealTerms',
        type: 'tableWithoutHeaders',
        columns: 2,
        rows: 0,
        children: [
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Amount' }],
                            },
                        ],
                    },
                    {
                        type: 'table-header',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.maximumDealAmount']
                                        ? { text: `${formData?.['dealTerms.maximumDealAmount']}` }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Rate of Interest' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.minimumRoi'] && formData?.['dealTerms.maximumRoi']
                                        ? {
                                              text:
                                                  formData?.['dealTerms.minimumRoi'] && formData?.['dealTerms.maximumRoi']
                                                      ? `${formData['dealTerms.minimumRoi']} - ${formData['dealTerms.maximumRoi']}%`
                                                      : '',
                                          }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Product' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.loanTypes']?.length
                                        ? {
                                              type: 'unorderedList',
                                              children: formData?.['dealTerms.loanTypes']?.map((type: any) => ({
                                                  type: 'list-item',
                                                  children: [
                                                      {
                                                          type: 'list-item',
                                                          children: [{ text: `${type ?? '-'}` }],
                                                      },
                                                  ],
                                              })),
                                          }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Tenure(months)' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.minimumTenure'] && formData?.['dealTerms.maximumTenure']
                                        ? {
                                              text:
                                                  formData?.['dealTerms.minimumTenure'] && formData?.['dealTerms.maximumTenure']
                                                      ? `${formData['dealTerms.minimumTenure']} - ${formData['dealTerms.maximumTenure']} months`
                                                      : '',
                                          }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Security' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.securityOffered']?.length
                                        ? {
                                              type: 'unorderedList',
                                              children: formData?.['dealTerms.securityOffered']?.map((security: any) => ({
                                                  type: 'list-item',
                                                  children: [{ text: `${security ?? '-'}` }],
                                              })),
                                          }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Deal Notes' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData?.['dealTerms.dealNotes']
                                        ? { text: `${formData?.['dealTerms.dealNotes'] ?? ''}` }
                                        : { text: DataNotAvl, bgColor: YELLOW },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    };
    table_preferredDealTerms.rows = table_preferredDealTerms?.children?.length;
    return table_preferredDealTerms;
};

export const updateTemplateDataWithPreferredTerms = (existingTemplateData, formData) => {
    let dataToUpdate = JSON.parse(JSON.stringify(existingTemplateData));
    // let table_preferredDealTerms = updatePreferredTermsInEditorData(formData);
    // if (table_preferredDealTerms) {
    //     dataToUpdate = [
    //         ...dataToUpdate,
    //         { type: 'headingThree', children: [{ text: 'Preferred Terms' }] },
    //         table_preferredDealTerms,
    //     ];
    // }
    let dealSummaryDataIndex = dataToUpdate.findIndex((table) => table?.key === 'table_dealSummary');
    if (dealSummaryDataIndex > -1) {
        let dealSummaryData = dataToUpdate[dealSummaryDataIndex]?.children;
        let sectorIndustryIndex = dealSummaryData?.findIndex((table) => table?.key === 'sector_industry');

        if (sectorIndustryIndex > -1) {
            dealSummaryData[sectorIndustryIndex] = {
                key: 'sector_industry',
                type: 'table-row',
                children: [
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [{ text: 'Sector/Industry' }],
                            },
                        ],
                    },
                    {
                        type: 'table-cell',
                        children: [
                            {
                                type: 'paragraph',
                                children: [
                                    formData && formData?.['borrowerProfile.companySector']
                                        ? {
                                              text: `${formData?.['borrowerProfile.companySector']}`,
                                              bold: true,
                                          }
                                        : {
                                              text: `${DataNotAvl}`,
                                              bgColor: YELLOW,
                                              bold: true,
                                          },
                                ],
                            },
                        ],
                    },
                ],
            };
        }
    }

    return dataToUpdate;
};

function formatCompanyMetricsValue(value: any, placeholder: string = '-'): string {
    if (value === null || value === undefined || value === '') {
        return placeholder;
    }
    const numericValue = parseFloat(String(value));
    if (!Number.isFinite(numericValue)) {
        return placeholder;
    }
    // Format value based on whether it has decimal digits
    const hasDecimal = numericValue % 1 !== 0;
    const absValue = Math.abs(numericValue);
    const formatted = hasDecimal ? Math.trunc(absValue * 10) / 10 : absValue.toString();
    // Format negative values with parentheses, positive values as-is
    return numericValue < 0 ? `(${formatted})` : `${formatted}`;
}

export function processAuditedFinancialUnsecuredLoanData(
    auditedFinancialUnsecuredData: { headers: any[]; rows: Array<Record<string, any>> } = { headers: [], rows: [] },
) {
    if (!auditedFinancialUnsecuredData) return null;
    let data = JSON.parse(JSON.stringify(auditedFinancialUnsecuredData));
    let headers = data?.headers ?? [];
    let rows = data?.rows ?? [];

    let debtRowToAdd = {
        classificationName: 'Unsecured Loans',
        label: 'Equity & Liabilities',
        parent: 'Debt',
        particular: 'Equity And Liabilities',
        subLabel: 'Non-current Liabilities',
    };

    rows.forEach((row: any) => {
        const keysToCollate = Object.entries(row).filter(
            ([k]) => !['classificationId', 'classificationName', 'id', 'label', 'parent', 'particular', 'subLabel'].includes(k),
        );
        keysToCollate.forEach(([key, val]) => {
            const num = Number(val);
            if (Number.isNaN(num)) return; // skip non-numeric
            // Fix floating point precision issues by rounding to 1 decimal place
            debtRowToAdd[key] = Number(((debtRowToAdd[key] ?? 0) + num).toFixed(1));
        });
    });

    data.rows.push(debtRowToAdd);
    return data;
}
